import React from 'react';
import { styled, SxProp, View } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { Platform } from 'react-native';

import { Image } from '../../common/Image';
import { Touchable, TouchableTypeEnum } from '../../common/Touchable';
import {
  DirectionCollage,
  DirectionCollageEnum,
} from './DirectionCollage';
import { createAspectRatioStyles } from '@styles';
import { CoverView } from '../../views/CoverView';
import { Icon } from '../../common/Icon';

const Container = styled(View)({
  ...createAspectRatioStyles(16 / 9, '100%'),
});

const TouchableContainer = styled(Touchable)({
  flex: 1,
});

const PostImageContainer = styled(View)({
  flex: 1,
  borderRadius: 12,
  bg: '$background20',
  overflow: 'hidden',
});

const PostImage = styled(Image)({
  flex: 1,
});

const PostImageThumbnail = styled(Image)({
  position: 'absolute',
  width: '100%',
  height: '100%',
});

const CoverViewContainer = styled(CoverView)({
  flex: 1,
});

const TutorialImageCoverBackground = styled(View)({
  zIndex: -1,
  position: 'absolute',
  width: '100%',
  height: '100%',
  bg: '$heading800',
  opacity: 0.7,
});

const coverViewStyles: SxProp = {
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 12,
  overflow: 'hidden',
};

interface ISource {
  uri?: string;
  thumbnailUri?: string;
}

export interface ICollageImageProps {
  sources: ISource[];
  onPress(index: number): void;
  isAvailableForCurrentPlan?: boolean;
}

export const CollageImage = observer<ICollageImageProps>(
  ({ sources, onPress, isAvailableForCurrentPlan }) => {
    function renderPostImage(start: number, end = 1) {
      return sources.slice(start, end).map((source, index) => {
        const imageIndex = start + index;

        return (
          <TouchableContainer
            key={index}
            onPress={() => onPress(imageIndex)}
            type={TouchableTypeEnum.WithoutFeedback}
          >
            <CoverViewContainer
              isVisible={!isAvailableForCurrentPlan}
              coverComponentSx={coverViewStyles}
              CoverComponent={() => (
                <React.Fragment>
                  <TutorialImageCoverBackground />
                  <Icon
                    name="lock-light"
                    color="$background0"
                    size={Platform.select({ default: 20, web: 30 })}
                  />
                </React.Fragment>
              )}
            >
              <PostImageContainer>
                {source.thumbnailUri && (
                  <PostImageThumbnail
                    source={{ uri: source.thumbnailUri }}
                  />
                )}
                <PostImage source={{ uri: source.uri }} />
              </PostImageContainer>
            </CoverViewContainer>
          </TouchableContainer>
        );
      });
    }

    if (sources.length === 0) {
      return null;
    }

    if (sources.length === 1) {
      return <Container>{renderPostImage(0)}</Container>;
    }

    if (sources.length === 2) {
      return (
        <Container>
          <DirectionCollage direction={DirectionCollageEnum.Row}>
            {renderPostImage(0, 2)}
          </DirectionCollage>
        </Container>
      );
    }

    if (sources.length === 3) {
      return (
        <Container>
          <DirectionCollage direction={DirectionCollageEnum.Row}>
            {renderPostImage(0)}
            <DirectionCollage>
              {renderPostImage(1, 3)}
            </DirectionCollage>
          </DirectionCollage>
        </Container>
      );
    }

    return (
      <Container>
        <DirectionCollage direction={DirectionCollageEnum.Row}>
          <DirectionCollage>{renderPostImage(0, 2)}</DirectionCollage>
          <DirectionCollage>{renderPostImage(2, 4)}</DirectionCollage>
        </DirectionCollage>
      </Container>
    );
  },
);
